import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../shared/services/auth.service';
import { UserProxy, SigninUserCommand } from '@/app/proxy/proxies';
import { ExternalProviderType } from '../shared/auth-enums';
import { UnifiedSsoService } from '../shared/services/unified-sso.service';
import { SSOProvider } from '../shared/interfaces/sso.interface';

@Component({
  selector: 'app-auth-callback',
  template: `
    <div class="auth-callback-container">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>{{ loadingMessage }}</p>
      </div>
    </div>
  `,
  styles: [
    `
      .auth-callback-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        background-color: #f5f5f5;
      }

      .loading-spinner {
        text-align: center;
      }

      .spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      p {
        color: #666;
        font-size: 16px;
      }
    `,
  ],
})
export class AuthCallbackComponent implements OnInit {
  loadingMessage: string = 'Processing authentication...';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private userProxy: UserProxy,
    private unifiedSsoService: UnifiedSsoService
  ) {}

  ngOnInit(): void {
    console.log('Auth callback component initialized');

    // Handle the callback from SSO providers
    this.handleCallback();
  }

  private async handleCallback(): Promise<void> {
    try {
      console.log('Processing SSO callback...');

      // Get query parameters
      const params = this.route.snapshot.queryParams;
      console.log('Callback parameters:', params);

      // Check if there's an error
      if (params['error']) {
        console.error('SSO callback error:', params['error'], params['error_description']);
        this.router.navigate(['/auth/login'], {
          queryParams: {
            error: 'sso_failed',
            message: params['error_description'] || 'SSO authentication failed',
          },
        });
        return;
      }

      // Determine which SSO provider this callback is for
      const ssoProvider = this.determineSsoProvider(params);

      if (!ssoProvider) {
        console.error('Could not determine SSO provider from callback parameters');
        this.router.navigate(['/auth/login'], {
          queryParams: {
            error: 'callback_failed',
            message: 'Invalid SSO callback parameters',
          },
        });
        return;
      }

      console.log('SSO provider detected:', ssoProvider);
      this.loadingMessage = `Authenticating with ${ssoProvider}...`;

      // Authenticate with the SSO provider
      const authResult = await this.authenticateWithSsoProvider(ssoProvider, params);

      if (authResult.success && authResult.token) {
        // Map SSO provider to external provider type
        const externalProviderType = this.mapSsoProviderToExternalType(ssoProvider);

        // Login with the backend using the SSO token
        await this.loginWithBackend(externalProviderType, authResult.token);
      } else {
        throw new Error('SSO authentication failed');
      }
    } catch (error) {
      console.error('Error handling SSO callback:', error);
      this.router.navigate(['/auth/login'], {
        queryParams: {
          error: 'callback_failed',
          message: error instanceof Error ? error.message : 'Failed to process authentication callback',
        },
      });
    }
  }

  private determineSsoProvider(params: any): SSOProvider | null {
    // Check for Microsoft callback (has code and state)
    if (params['code'] && params['state']) {
      return SSOProvider.MICROSOFT;
    }

    // Check for Google callback (has access_token or id_token)
    if (params['access_token'] || params['id_token']) {
      return SSOProvider.GOOGLE;
    }

    // Check for Apple callback (has authorization code)
    if (params['authorization_code']) {
      return SSOProvider.APPLE;
    }

    // Check for Facebook callback (has access_token)
    if (params['access_token'] && params['expires_in']) {
      return SSOProvider.FACEBOOK;
    }

    return null;
  }

  private async authenticateWithSsoProvider(
    ssoProvider: SSOProvider,
    params: any
  ): Promise<{ success: boolean; token?: string }> {
    try {
      // For Microsoft, the MSAL library should handle the redirect automatically
      if (ssoProvider === SSOProvider.MICROSOFT) {
        // MSAL handles the redirect automatically, so we can authenticate directly
        const authResponse = await this.unifiedSsoService.authenticate(ssoProvider).toPromise();
        return {
          success: authResponse.success,
          token: authResponse.token,
        };
      }

      // For other providers, we need to handle the callback parameters
      // This is a simplified approach - you might need to adjust based on your specific needs
      if (ssoProvider === SSOProvider.GOOGLE && params['access_token']) {
        return {
          success: true,
          token: params['access_token'],
        };
      }

      if (ssoProvider === SSOProvider.APPLE && params['authorization_code']) {
        return {
          success: true,
          token: params['authorization_code'],
        };
      }

      if (ssoProvider === SSOProvider.FACEBOOK && params['access_token']) {
        return {
          success: true,
          token: params['access_token'],
        };
      }

      return { success: false };
    } catch (error) {
      console.error(`Error authenticating with ${ssoProvider}:`, error);
      return { success: false };
    }
  }

  private mapSsoProviderToExternalType(ssoProvider: SSOProvider): ExternalProviderType {
    switch (ssoProvider) {
      case SSOProvider.GOOGLE:
        return ExternalProviderType.Google;
      case SSOProvider.MICROSOFT:
        return ExternalProviderType.Microsoft;
      case SSOProvider.APPLE:
        return ExternalProviderType.Apple;
      case SSOProvider.FACEBOOK:
        return ExternalProviderType.Facebook;
      default:
        throw new Error(`Unknown SSO provider: ${ssoProvider}`);
    }
  }

  private async loginWithBackend(externalProviderType: ExternalProviderType, token: string): Promise<void> {
    try {
      this.loadingMessage = 'Logging you in...';

      // Create the signin command with SSO credentials
      const user = new SigninUserCommand({
        email: '',
        phone: '',
        phoneCode: '',
        password: '',
        newPassword: undefined,
        externalProviderType: externalProviderType,
        externalProviderToken: token,
        captchaToken: undefined,
      });

      // Call the backend to authenticate
      const response = await this.userProxy.user_Signin(user).toPromise();

      if (response.isSuccess) {
        // Handle successful login
        this.authService.handleSigninResponse(response);
        this.authService.handlePostLoginRedirect();
      } else {
        // Handle login failure
        const errorMessage = response.errors?.[0]?.message?.split(':')[1] || 'Login failed';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Backend login failed:', error);
      throw error;
    }
  }
}
